import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import type { Locale } from "date-fns";
import { de, enGB, es, ru as ruLoc, uk } from "date-fns/locale";
import { addDays, format, parseISO, startOfDay } from "date-fns";
import { Card } from "@/components/ui/Card";
import { SPORT_COLOR as SPORT_COLORS, THEME } from "@/lib/theme";
import type { WorkoutSourceAvailability } from "@/types";

/**
 * Minimal shape a workout needs to have to render on the calendar.
 * Both `PlannedWorkout` (from /plan/workouts) and `WorkoutSummary` (from
 * /workouts) can be adapted into this.
 */
export interface CalendarWorkout extends WorkoutSourceAvailability {
  id: number;
  date: string; // ISO yyyy-mm-dd
  sport: string;
  title: string;
  duration_min?: number | null;
  tss?: number | null;
  status?: string | null; // "skeleton" → dashed/faded; anything else → solid
}

export interface CalendarWeekMeta {
  weekNumber: number;
  startDate: string; // ISO Monday
  phase?: string | null;
  targetTss?: number | null;
}

interface Props {
  weeks: CalendarWeekMeta[];
  workouts: CalendarWorkout[];
  emptyMessage?: string;
  /** Prefix for workout links. Default: "/workout/". Use "/workout/planned/"
   *  when rendering planned workouts (from /plan/workouts). */
  linkPrefix?: string;
  /** Owner-local date supplied by plan pages, independent of browser timezone. */
  today?: string;
}


const _DATE_LOCALES_CV: Record<string, Locale> = { en: enGB, ru: ruLoc, uk, es, de };
function _localeForCV(): Locale {
  const lng = (i18n.language || "en").slice(0, 2).toLowerCase();
  return _DATE_LOCALES_CV[lng] || enGB;
}
const WEEKDAY_LABELS_RU = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
function weekdayShort(idx: number): string {
  const monRef = new Date(1970, 0, 5);
  const d = new Date(monRef);
  d.setDate(monRef.getDate() + idx);
  return format(d, "EEEEEE", { locale: _localeForCV() });
}


function sportColor(sport: string): string {
  return SPORT_COLORS[sport] ?? THEME.mutedLight;
}


export function CalendarView({ weeks, workouts, emptyMessage, linkPrefix = "/workout/", today }: Props) {
  const { t } = useTranslation();
  if (!weeks.length) {
    return (
      <Card>
        <div className="py-4 text-center text-[13px] text-taupe">
          {emptyMessage || t("plan.empty_msg")}
        </div>
      </Card>
    );
  }

  // Index workouts by ISO date string (yyyy-mm-dd) for O(1) lookup.
  const byDate = new Map<string, CalendarWorkout[]>();
  for (const w of workouts) {
    const key = w.date.slice(0, 10);
    const arr = byDate.get(key);
    if (arr) arr.push(w);
    else byDate.set(key, [w]);
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[880px] space-y-1">
        {/* Sticky header row */}
        <div className="sticky top-0 z-10 grid grid-cols-[84px_repeat(7,1fr)_84px] gap-1 bg-cream py-1">
          <div className="text-[10px] font-medium uppercase tracking-wider text-taupe">
            Неделя
          </div>
          {WEEKDAY_LABELS_RU.map((d) => (
            <div
              key={d}
              className="text-center text-[10px] font-medium uppercase tracking-wider text-taupe"
            >
              {d}
            </div>
          ))}
          <div className="text-right text-[10px] font-medium uppercase tracking-wider text-taupe">
            TSS
          </div>
        </div>

        {weeks.map((wk) => {
          const weekStart = startOfDay(parseISO(wk.startDate));
          return (
            <div
              key={wk.weekNumber}
              className="grid grid-cols-[84px_repeat(7,1fr)_84px] gap-1"
            >
              {/* Week label */}
              <Link
                to={`/plan/week/${wk.weekNumber}`}
                className="flex flex-col justify-center rounded-md px-2 py-1 hover:bg-amber/5"
              >
                <div className="text-[13px] font-medium text-navy-deep">
                  W{wk.weekNumber}
                </div>
                {wk.phase ? (
                  <div className="text-[10px] uppercase tracking-wider text-amber-dark">
                    {wk.phase}
                  </div>
                ) : null}
                <div className="text-[10px] text-taupe">
                  {format(weekStart, "d MMM", { locale: _localeForCV() })}
                </div>
              </Link>

              {/* 7 day cells */}
              {Array.from({ length: 7 }).map((_, i) => {
                const day = addDays(weekStart, i);
                const key = format(day, "yyyy-MM-dd");
                const items = byDate.get(key) || [];
                const isToday = key === (today ?? format(new Date(), "yyyy-MM-dd"));
                return (
                  <div
                    key={key}
                    aria-current={isToday ? "date" : undefined}
                    className={
                      (isToday
                        ? "border-amber ring-1 ring-amber/30 "
                        : "border-cream-border ") +
                      "flex min-h-[80px] flex-col gap-1 rounded-md border bg-cream-light p-1.5"
                    }
                  >
                    <div className="text-[10px] text-taupe">
                      {format(day, "d MMM", { locale: _localeForCV() })}
                    </div>
                    {items.map((w) => {
                      const isSkel = w.status === "skeleton";
                      return (
                        <Link
                          key={w.id}
                          to={`${linkPrefix}${w.id}`}
                          className={
                            "block rounded px-1.5 py-1 text-[11px] leading-tight hover:bg-amber/10 " +
                            (isSkel
                              ? "border-l-2 border-dashed bg-cream-light/30 opacity-70"
                              : "border-l-2 bg-cream-light/60")
                          }
                          style={{ borderLeftColor: w.source_unavailable ? THEME.mutedLight : sportColor(w.sport) }}
                        >
                          <div className="truncate font-medium text-navy-deep">
                            {w.source_unavailable ? t("workout_detail.source_unavailable") : w.title || w.sport}
                          </div>
                          <div className="flex items-center gap-1 text-[10px] text-taupe">
                            {w.source_unavailable ? <span>Strava · Intervals.icu</span> : <span>
                              {w.duration_min ? `${w.duration_min}м` : ""}
                              {w.duration_min && w.tss ? " · " : ""}
                              {w.tss ? `${w.tss} TSS` : ""}
                            </span>}
                            {isSkel ? (
                              <span className="ml-auto rounded bg-cream-border px-1 text-[8px] uppercase tracking-wider text-taupe">
                                {t("workout_detail.preliminary")}
                              </span>
                            ) : null}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                );
              })}

              {/* Weekly summary: target TSS + planned workouts count */}
              <div className="flex flex-col justify-center px-2 py-1 text-right">
                {wk.targetTss != null ? (
                  <>
                    <div className="metric-number text-[15px]">{wk.targetTss}</div>
                    <div className="text-[10px] text-taupe">TSS</div>
                  </>
                ) : (
                  <div className="text-[11px] text-taupe">—</div>
                )}
                {(() => {
                  let count = 0;
                  for (let i = 0; i < 7; i++) {
                    const key = format(addDays(weekStart, i), "yyyy-MM-dd");
                    count += byDate.get(key)?.filter((w) => !w.source_unavailable).length ?? 0;
                  }
                  return count > 0 ? (
                    <div className="mt-1 text-[10px] text-taupe">
                      {count} wrk
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
